import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import {
  fetchAddressById,
  fetchBuildingsByIdentifier,
  fetchLandParcelsCadastresId,
} from "data/queries-common";
import { X } from "lucide-react";
import { useRef, useState } from "react";
import Draggable from "react-draggable";
import { MapboxGeoJSONFeature } from "react-map-gl";
import { useQuery } from "@tanstack/react-query";
import { resolveIdentityId } from "utils/identity";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { EasementsTable } from "./details/easements-table";
import { KeyValueDetails } from "./details/key-value-details";

interface FeatureCardProps {
  intersectingEasements: MapboxGeoJSONFeature[] | undefined;
  cadastrePid: string;
  addressId: string;
  onClose: () => void;
}

export const FeatureCard = (props: FeatureCardProps) => {
  const [identityState] = useIdentity();

  const identityId = resolveIdentityId(identityState);

  const initialPosition = {
    x: Math.min(window.innerWidth * 0.7, window.innerWidth - 1000),
    y: window.innerHeight * 0.1,
  };
  const [position, setPosition] = useState(initialPosition);
  const nodeRef = useRef(null);

  const addressByIdQuery = useQuery({
    queryKey: [QueryKeys.addressById, props.addressId],
    queryFn: () => fetchAddressById(props.addressId),
    enabled: !!props.addressId,
  });

  const buildingsQuery = useQuery({
    queryKey: [
      QueryKeys.buildingsKey,
      identityId,
      "cadastre",
      props.cadastrePid,
    ],
    queryFn: () =>
      fetchBuildingsByIdentifier("cadastrePid", props.cadastrePid, [
        "poolIndicator",
        "solarIndicator",
      ]),
    enabled: !!props.cadastrePid,
  });

  const landParcelsCadastresQuery = useQuery({
    queryKey: [
      QueryKeys.landParcelsCadastresKey,
      identityId,
      props.cadastrePid,
    ],
    queryFn: () => fetchLandParcelsCadastresId(props.cadastrePid),
    enabled: !!props.cadastrePid,
  });

  return (
    <Draggable
      nodeRef={nodeRef}
      handle=".drag-handle"
      position={position}
      onStop={(_, data) => setPosition({ x: data.x, y: data.y })}
    >
      <div ref={nodeRef} className="fixed left-0 top-0 z-1000">
        <Card className="w-150">
          <CardHeader className="drag-handle flex cursor-move flex-row items-center justify-between border-none p-2">
            <CardTitle className="text-sm font-medium">
              {addressByIdQuery.data?.address.properties.formatted_address}
            </CardTitle>
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              onClick={props.onClose}
            >
              <X className="size-4" />
            </Button>
          </CardHeader>
          <CardContent className="w-full">
            <div className="flex w-full flex-col gap-2">
              <div className="flex w-full flex-row gap-2">
                {landParcelsCadastresQuery.isError && (
                  <Alert variant="destructive">
                    <AlertTitle>Parcel Details not found.</AlertTitle>
                  </Alert>
                )}
                {landParcelsCadastresQuery.isEnabled &&
                  landParcelsCadastresQuery.isPending && (
                    <Skeleton className="h-75 w-full" />
                  )}
                {landParcelsCadastresQuery.isSuccess && (
                  <KeyValueDetails
                    header="Parcel Details"
                    countText={`Parcel Count: ${landParcelsCadastresQuery.data.features.length}`}
                    properties={{
                      "Parcel Id":
                        landParcelsCadastresQuery.data.features[0].properties
                          ?.parcelId,
                      "Parcel Type":
                        landParcelsCadastresQuery.data.features[0].properties
                          ?.parcelType,
                      "Title Status":
                        landParcelsCadastresQuery.data.features[0].properties
                          ?.titleStatus,
                      "Contributor Id":
                        landParcelsCadastresQuery.data.features[0].properties
                          ?.contributorId,
                      "Cadastre Pid":
                        landParcelsCadastresQuery.data.features[0].properties
                          ?.cadastrePid,
                      "Area": `${landParcelsCadastresQuery.data.features[0].properties?.area} m²`,
                    }}
                  />
                )}
                {buildingsQuery.isSuccess && (
                  <KeyValueDetails
                    header="Primary Building Details"
                    countText={`Building Count: ${buildingsQuery.data.features.length}`}
                    properties={{
                      "Ground Elevation":
                        buildingsQuery.data.features[0].properties
                          ?.groundElevation,
                      "Swimming Pools":
                        buildingsQuery.data.features[0].properties
                          ?.poolIndicator?.swimmingPoolAdjacent,
                      "Solar Panels":
                        buildingsQuery.data.features[0].properties
                          ?.solarIndicator?.solarPanel,
                      "Planning Zone":
                        buildingsQuery.data.features[0].properties
                          ?.planningZone,
                      "Building Pid":
                        buildingsQuery.data.features[0].properties?.buildingPid,
                      "Area": `${buildingsQuery.data.features[0].properties?.area} m²`,
                    }}
                  />
                )}
              </div>
              {props.intersectingEasements &&
                props.intersectingEasements.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <Alert variant="destructive" className="border-warning">
                      <AlertTitle>Parcel contains easements</AlertTitle>
                    </Alert>
                    <EasementsTable
                      intersectingEasements={props.intersectingEasements}
                    />
                  </div>
                )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Draggable>
  );
};
